import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { usePermissions } from './contexts/PermissionsContext';
import { useCompany } from './contexts/CompanyContext';
import Login from './pages/Login';
import Register from './pages/Register';
import PublicTicketView from './pages/PublicTicketView';
import AppLayout from './components/AppLayout';
import Home from './pages/Home';
import TicketList from './pages/TicketList';
import TicketCreate from './pages/TicketCreate';
import TicketDetail from './pages/TicketDetail';
import MyTasks from './pages/MyTasks';
import KnowledgeBase from './pages/KnowledgeBase';
import ArticleDetail from './pages/ArticleDetail';
import ArticleEditor from './pages/ArticleEditor';
import Reports from './pages/Reports';
import CompanySettings from './pages/CompanySettings';
import Profile from './pages/Profile';
import Admin from './pages/Admin';
import CrmDashboard from './pages/crm/CrmDashboard';
import ContactList from './pages/crm/ContactList';
import ContactCreate from './pages/crm/ContactCreate';
import ContactDetail from './pages/crm/ContactDetail';
import OrganizationList from './pages/crm/OrganizationList';
import OrganizationCreate from './pages/crm/OrganizationCreate';
import OrganizationDetail from './pages/crm/OrganizationDetail';
import DealBoard from './pages/crm/DealBoard';
import DealDetail from './pages/crm/DealDetail';
import ActivityList from './pages/crm/ActivityList';
import Inbox from './pages/crm/Inbox';
import CrmWork from './pages/crm/Work';
import ConversationDetail from './pages/crm/ConversationDetail';

// Portal
import PortalLayout from './components/PortalLayout';
import PortalDashboard from './pages/portal/PortalDashboard';
import PortalTicketList from './pages/portal/PortalTicketList';
import PortalTicketCreate from './pages/portal/PortalTicketCreate';
import PortalTicketDetail from './pages/portal/PortalTicketDetail';
import PortalKnowledgeBase from './pages/portal/PortalKnowledgeBase';
import PortalArticleDetail from './pages/portal/PortalArticleDetail';
import PortalProfile from './pages/portal/PortalProfile';
import PortalJoinCompany from './pages/portal/PortalJoinCompany';
import PortalJoinByCode from './pages/portal/PortalJoinByCode';
import PortalRegister from './pages/portal/PortalRegister';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div className="flex min-h-screen items-center justify-center">Loading...</div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/**
 * Gates the staff app on the user's role in the ACTIVE company. A user who is
 * staff in one company but a customer in the currently-selected one is sent to
 * the portal — the staff shell is no longer shown for a company where their
 * role is customer. (Previously this only redirected users who were customers
 * in EVERY company, leaking the staff UI.)
 */
/**
 * The portal page that corresponds to a staff-app path, so a customer-role
 * member (an outside collaborator or task assignee) who follows a staff link
 * — an email, a shared URL — lands on the same ticket in the portal instead
 * of the portal home.
 */
function portalPathFor(pathname: string): string {
  const ticket = pathname.match(/^\/tickets\/([^/]+)\/?$/);
  if (ticket && ticket[1] !== 'new') return `/portal/tickets/${ticket[1]}`;
  if (pathname === '/tickets') return '/portal/tickets';
  if (pathname === '/tickets/new') return '/portal/tickets/new';
  return '/portal';
}

function StaffOrRedirect({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { isStaff } = usePermissions();
  const { isLoading, currentCompany, userCompanies } = useCompany();
  const location = useLocation();
  const loadingEl = <div className="flex min-h-screen items-center justify-center">Loading...</div>;

  // Super admins always get the staff app, regardless of company-load timing.
  if ((user as any)?.isSuperAdmin) return <>{children}</>;

  if (isLoading) return loadingEl;

  // Role is null until the active company is resolved (see PermissionsContext),
  // which momentarily makes even an owner/super-admin look non-staff. Wait for
  // currentCompany before deciding — otherwise staff get bounced to /portal.
  if (!currentCompany) {
    if (userCompanies.length > 0) return loadingEl;   // transient: selection still settling
    return <Navigate to="/portal" replace />;          // genuinely no company → join via portal
  }

  if (!isStaff) return <Navigate to={portalPathFor(location.pathname)} replace />;
  return <>{children}</>;
}

export default function App() {
  const { currentCompany } = useCompany();
  const companyKey = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid ?? 'none';

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/portal/register" element={<PortalRegister />} />
      <Route path="/t/:token" element={<PublicTicketView />} />

      {/* Staff routes — portal-only users get redirected */}
      <Route
        key={companyKey}
        path="/"
        element={
          <ProtectedRoute>
            <StaffOrRedirect>
              <AppLayout />
            </StaffOrRedirect>
          </ProtectedRoute>
        }
      >
        <Route index element={<Home />} />
        <Route path="tickets" element={<TicketList />} />
        <Route path="tickets/new" element={<TicketCreate />} />
        <Route path="tickets/:id" element={<TicketDetail />} />
        <Route path="my-work" element={<MyTasks />} />
        <Route path="kb" element={<KnowledgeBase />} />
        <Route path="kb/new" element={<ArticleEditor />} />
        <Route path="kb/:id/edit" element={<ArticleEditor />} />
        <Route path="kb/:slug" element={<ArticleDetail />} />
        <Route path="reports" element={<Reports />} />
        <Route path="settings/company" element={<CompanySettings />} />
        <Route path="profile" element={<Profile />} />
        <Route path="admin" element={<Admin />} />
        {/* CRM */}
        <Route path="crm" element={<CrmDashboard />} />
        <Route path="crm/inbox" element={<Inbox />} />
        <Route path="crm/work" element={<CrmWork />} />
        <Route path="crm/inbox/:id" element={<ConversationDetail />} />
        <Route path="crm/contacts" element={<ContactList />} />
        <Route path="crm/contacts/new" element={<ContactCreate />} />
        <Route path="crm/contacts/:id" element={<ContactDetail />} />
        <Route path="crm/organizations" element={<OrganizationList />} />
        <Route path="crm/organizations/new" element={<OrganizationCreate />} />
        <Route path="crm/organizations/:id" element={<OrganizationDetail />} />
        <Route path="crm/deals/board" element={<DealBoard />} />
        <Route path="crm/deals/:id" element={<DealDetail />} />
        <Route path="crm/activities" element={<ActivityList />} />
      </Route>

      {/* Portal routes */}
      <Route
        path="/portal"
        element={
          <ProtectedRoute>
            <PortalLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<PortalDashboard />} />
        <Route path="tickets" element={<PortalTicketList />} />
        <Route path="tickets/new" element={<PortalTicketCreate />} />
        <Route path="tickets/:id" element={<PortalTicketDetail />} />
        <Route path="kb" element={<PortalKnowledgeBase />} />
        <Route path="kb/:slug" element={<PortalArticleDetail />} />
        <Route path="profile" element={<PortalProfile />} />
        <Route path="join" element={<PortalJoinCompany />} />
        <Route path="join/:code" element={<PortalJoinByCode />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
